import { useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { useAppWindowScrollStore } from 'src/app/router/layouts/navigation/app-window-frame-business'
import { VerificationBadge } from 'src/shared/components/icons/verification-badge'
import TruncatedText from 'src/shared/components/ui/miscellaneous/truncated-text'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import { useUserByUsername } from 'src/shared/features/users/api/accounts/use-user-by-username'
import { usePublicLicenses } from 'src/shared/features/users/api/licenses/use-public-licenses'
import { useUserReviews } from 'src/shared/features/users/api/marketplace/use-user-reviews'
import { ExpertiseIcon } from 'src/shared/features/users/assets/general-info/expertise'
import { LanguagesIcon } from 'src/shared/features/users/assets/general-info/languages'
import { VerifiedLicensesIcon } from 'src/shared/features/users/assets/general-info/licenses'
import { WorksAtIcon } from 'src/shared/features/users/assets/general-info/works-at'
import UserProfileCard from 'src/shared/features/users/components/cards/user-profile-card'
import CoverPhoto from 'src/shared/features/users/components/profile-photos/cover-photo'
import { ProfileAvatar } from 'src/shared/features/users/components/profile-photos/profile-avatar'
import BioModal from 'src/shared/features/users/components/public-profile/bio/bio-modal'
import UserFeedList from 'src/shared/features/users/components/public-profile/feed/user-feed-list'
import GeneralInfoSection from 'src/shared/features/users/components/public-profile/general-info-section'
import ListingList from 'src/shared/features/users/components/public-profile/listings/listing-list'
import OtherInfoContainer from 'src/shared/features/users/components/public-profile/other-info-container'
import ProfileOverview from 'src/shared/features/users/components/public-profile/overview/profile-overview'
import PortfolioList from 'src/shared/features/users/components/public-profile/portfolio/portfolio-list'
import ReviewList from 'src/shared/features/users/components/public-profile/reviews/review-list'
import { expertiseMap } from 'src/shared/features/users/schemas/expertise'
import type { Expertise } from 'src/shared/features/users/schemas/expertise'
import { languageMap } from 'src/shared/features/users/schemas/language'
import type { Language } from 'src/shared/features/users/schemas/language'
import type { License, State } from 'src/shared/features/users/schemas/licenses'
import { professionMap } from 'src/shared/features/users/schemas/profession'
import type { Profession } from 'src/shared/features/users/schemas/profession'
import { statesMap } from 'src/shared/features/users/schemas/states'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'

import SharedBusinessSection from '../components/shared-business-section'
import ProfileTabBar from '../components/ui/profile-tab-bar'

const DESKTOP_TAB_BAR_HEIGHT = 65
const TABLET_TAB_BAR_HEIGHT = 65
const MOBILE_TAB_BAR_HEIGHT = 55

const DESKTOP_WINDOW_TOP_OFFSET = 29
const TABLET_WINDOW_TOP_OFFSET = 70
const MOBILE_WINDOW_TOP_OFFSET = 70

const TOP_PADDING = 16

const InAppUserProfileView = ({ username }: { username: string }) => {
  const { state } = useLocation()
  const { data: currentUser } = useMyUserData()
  const { data: otherUser } = useUserByUsername(username, state?.from === 'search')
  const { data: licenses } = usePublicLicenses(otherUser?.id || 0)
  const { appWindowScrollContainer } = useAppWindowScrollStore()

  const [isBioModalOpen, setIsBioModalOpen] = useState(false)
  const navigate = useNavigate()
  const { isMobile, isSmTabletOrSmaller } = useBreakpoint()

  const tabScrollOffset = isMobile
    ? MOBILE_WINDOW_TOP_OFFSET + MOBILE_TAB_BAR_HEIGHT + TOP_PADDING
    : isSmTabletOrSmaller
    ? TABLET_WINDOW_TOP_OFFSET + TABLET_TAB_BAR_HEIGHT + TOP_PADDING
    : DESKTOP_TAB_BAR_HEIGHT + DESKTOP_WINDOW_TOP_OFFSET + TOP_PADDING

  const isCurrentUser = currentUser?.username === username
  const user = isCurrentUser ? currentUser : otherUser

  const { data: reviewData } = useUserReviews(user?.id || -1)
  const { data: publicUserLicenses } = usePublicLicenses(user?.id || -1)

  const portfolioRef = useRef<HTMLDivElement>(null)
  const listingsRef = useRef<HTMLDivElement>(null)
  const reviewsRef = useRef<HTMLDivElement>(null)
  const activityRef = useRef<HTMLDivElement>(null)
  const overviewRef = useRef<HTMLDivElement>(null)

  if (!user) {
    navigate('/404')
    return null
  }

  const hasApprovedLicense = licenses?.some((license: License) => license.status === 'approved')
  const isVerified = user.verified && hasApprovedLicense
  const reviewsCount = reviewData?.totalReviews ?? 0
  const averageRating = reviewData?.averageRating ?? 0
  const isLoanOfficer = user?.profession === 'mortgage_loan_officer'

  if (isSmTabletOrSmaller) {
    return (
      <div>
        <div className="flex min-h-screen flex-col">
          <header className="relative">
            <CoverPhoto userId={user.id} className="h-[194px] w-full" />
            <div className="mb-[-25px] flex w-full translate-y-[-25px] flex-row justify-center rounded-[20px] bg-white text-primary">
              <div className="mb-[-50px] flex w-full translate-y-[-50px] flex-col items-center gap-2 px-5 md:px-8">
                <ProfileAvatar
                  userId={user.id}
                  size={88}
                  className="border-4 border-white shadow-[0_1px_6px_1px_rgba(27,48,41,0.10)]"
                  shouldZoom={true}
                />
                <div className="flex w-full flex-col items-center gap-[10px]">
                  <div className="flex flex-col items-center gap-[2px]">
                    <div className="flex items-center gap-2">
                      <h1 className={'text-[24px] font-bold md:text-[32px]'}>{user.fullName}</h1>
                      <VerificationBadge isVerified={isVerified || false} className="mt-0.5" size={24} />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-primary md:text-[17px]">{professionMap[user.profession].label}</p>
                    </div>
                  </div>
                  {!user.biography ? (
                    <div className="relative w-full text-center text-sm italic text-muted md:text-[17px]">
                      <p className="line-clamp-1">{user.fullName} doesn&apos;t have a bio yet.</p>
                    </div>
                  ) : (
                    <TruncatedText
                      key={'mobile-bio'}
                      text={user.biography || ''}
                      numberOfLines={2}
                      customOverflowComponent={
                        <button
                          className="inline bg-white pl-1 text-primary underline"
                          onClick={() => setIsBioModalOpen(true)}
                        >
                          Read full bio
                        </button>
                      }
                      className="relative w-full max-w-[600px] text-center text-[13px] leading-[150%] md:text-[14px]"
                    />
                  )}
                  <OtherInfoContainer
                    isCurrentUser={false}
                    reviewsCount={reviewsCount}
                    averageRating={averageRating}
                    shortAddress={user.shortAddress || ''}
                    className="text-[12px] md:text-[14px]"
                  />
                </div>
              </div>
              <BioModal isOpen={isBioModalOpen} setOpen={setIsBioModalOpen} user={user} />
            </div>
          </header>
          <main className="my-7 flex flex-col gap-7 px-5 md:px-8">
            <div className="h-px w-full bg-lightBorder" />
            <div className="flex flex-col gap-[26px] px-[6px]">
              <GeneralInfoSection
                type="mobile"
                icon={<VerifiedLicensesIcon className="size-7 text-primary md:size-8" />}
                headingClassName="mb-1"
                className="md:gap-4"
                title="Verified Licenses"
                subtitle={isLoanOfficer ? publicUserLicenses?.[0].licenseNumber || undefined : undefined}
                text={publicUserLicenses
                  ?.map((license) =>
                    isLoanOfficer
                      ? `${statesMap[license.state as unknown as State]?.label}`
                      : `${statesMap[license.state as unknown as State]?.label} (#${license.licenseNumber})`,
                  )
                  .join(` • `)}
              />
              {user?.companyName && (
                <GeneralInfoSection
                  type="mobile"
                  headingClassName="mb-1"
                  className="md:gap-4"
                  icon={<WorksAtIcon className="size-7 text-primary md:size-8" />}
                  title="Works at"
                  text={user?.companyName || 'N/A'}
                />
              )}
              {user?.expertise && user.expertise?.length > 0 && (
                <GeneralInfoSection
                  type="mobile"
                  headingClassName="mb-1"
                  className="md:gap-4"
                  icon={<ExpertiseIcon className="size-7 text-primary md:size-8" />}
                  title="Expertise"
                  text={user?.expertise
                    ?.map(
                      (e) => expertiseMap[user?.profession as unknown as Profession][e as unknown as Expertise]?.label,
                    )
                    .join(` • `)}
                />
              )}
              <GeneralInfoSection
                type="mobile"
                headingClassName="mb-1"
                className="md:gap-4"
                icon={<LanguagesIcon className="size-7 text-primary md:size-8" />}
                title="Languages"
                text={user?.languages?.map((e) => languageMap[e as unknown as Language]?.label).join(` • `)}
              />
            </div>
            <div className="mx-auto flex max-w-[1000px] flex-col">
              <ProfileTabBar
                sections={[
                  { id: 'overview', label: 'Overview', ref: overviewRef },
                  { id: 'reviews', label: 'Reviews', ref: reviewsRef },
                  { id: 'portfolio', label: 'Portfolio', ref: portfolioRef },
                  { id: 'activity', label: 'Activity', ref: activityRef },
                ]}
                scrollContainer={appWindowScrollContainer}
                tabScrollOffset={tabScrollOffset}
                defaultActiveTab="overview"
                sticky={true}
              />
              <div ref={overviewRef}>
                <ProfileOverview userId={user.id} />
              </div>
              <div ref={listingsRef}>
                <ListingList showAll={true} firstName={user.firstName} username={username as string} />
              </div>
              <div ref={reviewsRef}>
                <ReviewList
                  showAll={true}
                  mobile
                  userId={user.id}
                  username={username as string}
                  firstName={user.firstName}
                  onlyShowReviewStatus={true}
                />
              </div>
              <div ref={portfolioRef}>
                <PortfolioList
                  showAll={true}
                  mobile
                  userId={user.id}
                  username={username as string}
                  firstName={user.firstName}
                  profession={user.profession}
                />
              </div>
              <div ref={activityRef}>
                <UserFeedList mobile userId={user.id} firstName={user.firstName} />
              </div>
            </div>
          </main>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex min-h-screen flex-col">
        <header className="relative mb-[16px]">
          <UserProfileCard user={user} buttons="professional" />
        </header>

        {isSmTabletOrSmaller && <div className="my-4 h-px w-full bg-lightBorder px-10" />}

        <main className="mx-auto mb-10 mt-[10px] flex max-w-[1000px] flex-col gap-10 px-10">
          <ProfileTabBar
            sections={[
              { id: 'overview', label: 'Overview', ref: overviewRef },
              { id: 'reviews', label: 'Reviews', ref: reviewsRef },
              { id: 'portfolio', label: 'Portfolio', ref: portfolioRef },
              { id: 'activity', label: 'Activity', ref: activityRef },
            ]}
            scrollContainer={appWindowScrollContainer}
            tabScrollOffset={tabScrollOffset}
            defaultActiveTab="overview"
            sticky={true}
          />

          <div className="flex flex-col gap-10">
            <div ref={overviewRef} className="flex flex-col gap-10">
              <SharedBusinessSection userId={user.id} />
              <ProfileOverview userId={user.id} />
            </div>
            <div ref={listingsRef}>
              <ListingList showAll={true} firstName={user.firstName} username={username as string} />
            </div>
            <div ref={reviewsRef}>
              <ReviewList showAll={true} userId={user.id} username={username as string} firstName={user.firstName} />
            </div>

            <div ref={portfolioRef}>
              <PortfolioList
                showAll={true}
                mobile
                userId={user.id}
                username={username as string}
                firstName={user.firstName}
                profession={user.profession}
              />
            </div>
            <div ref={activityRef}>
              <UserFeedList userId={user.id} firstName={user.firstName} />
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default InAppUserProfileView
